import { BookMarked, Building2, Package, Ruler, Tags, Warehouse } from 'lucide-react';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import {
  SettingsMenu,
  type SettingsMenuEntry,
} from '../../../../components/settings/SettingsMenu';

/*
  What the Inventory module has to configure. One row per surface, each leading to its own page
  — the lists are deliberately not inlined here, because a reader opens Settings to find out
  what there is to configure and a page of stacked tables answers a different question.

  Every row belongs to a DIFFERENT module and carries its own key, which is why the page itself
  is gated on holding ANY of them: one of them would hide it from a reader entitled to another.

  ITEMS LEADS, and it arrived here on 18 Sep 2026 having been the module's first rail entry.
  The test is the one a Settings row always takes — does a reader go there to DO something, or
  to change what the doing draws on? While Items was the only surface in the group the answer
  was obviously the first. With Stock Batches, Stock Units and Stock Levels in the rail it is
  the second: a lot, a unit and a level are each filed against an item, every stock form picks
  one, and nothing is done to the item itself. It is first in this menu rather than last
  because the three vocabularies under it are what an ITEM is built from — the catalog, then
  its parts.

  The three VOCABULARIES exist only here, and that is load-bearing rather than tidy:
  `inventory-items.read` implies all three unscoped, which is correct precisely because none has
  a sidebar row for that grant to arrive at. Promoting one to the rail would hand every item
  reader a module they were never granted. **Items moving into this menu does not touch that** —
  the three still have no rail row of their own, which is the whole of what the reasoning rests
  on.

  Storage Facilities is here on a DIFFERENT argument, and it is worth keeping the two apart:
  nothing implies its read, so no grant turns on where it sits. It is here on the work-versus-
  configuration test alone — the same test that has now moved Items. It sits LAST because the
  three above it are the item catalog's own vocabulary and read as a set; a place is a different
  subject.
*/
const ENTRIES: SettingsMenuEntry[] = [
  {
    key: 'items',
    label: 'Items',
    description: 'The catalog of things the organization stocks. Every lot, unit and level is filed against one.',
    icon: Package,
    to: '/inventory/settings/items',
    permission: 'inventory-items.read',
  },
  {
    key: 'categories',
    label: 'Categories',
    description: 'What an item is — the classification every catalog item is filed under.',
    icon: Tags,
    to: '/inventory/settings/categories',
    permission: 'inventory-categories.read',
  },
  {
    key: 'item-types',
    label: 'Item Types',
    description: 'The governed vocabulary of item names, so one thing is not recorded four ways.',
    icon: BookMarked,
    to: '/inventory/settings/item-types',
    permission: 'inventory-item-types.read',
  },
  {
    key: 'units',
    label: 'Units of Measure',
    description: 'What stock is counted in. Quantities are summed by it, so it is a catalog rather than free text.',
    icon: Ruler,
    to: '/inventory/settings/units',
    permission: 'units-of-measure.read',
  },
  {
    key: 'storage-facilities',
    label: 'Storage Facilities',
    description: 'The places stock sits, and the stores nested inside them.',
    icon: Warehouse,
    to: '/inventory/settings/storage-facilities',
    permission: 'storage-facilities.read',
  },
  {
    /*
      The real-property REGISTER, and it is a row here rather than in the rail on the standing
      test: a property is filed against a catalog type, and what a reader does with the rail's
      three stock surfaces is not done to a building. It sits LAST because a new row is appended
      rather than slotted in — the position of every entry above it is one somebody has learned.
    */
    key: 'real-properties',
    label: 'Real Properties',
    description: 'The buildings and leased spaces the organization holds, each under its property type.',
    icon: Building2,
    to: '/inventory/settings/real-properties',
    permission: 'real-properties.read',
  },
];

/**
 * Inventory → Settings: a **menu of what the module configures**, not the settings themselves.
 *
 * The sidebar carries one entry per module rather than one per configurable list, and this page
 * is where that entry expands. Each row leads to a full page with its own breadcrumb, toolbar,
 * Create and settings cog.
 */
export function InventorySettingsPage() {
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      <Breadcrumb segments={['Logistics', 'Inventory', 'Settings']} />

      <div className="modern-scrollbar min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-3">
          {/*
            One line, and it says what the menu cannot: which of these rows the reader probably
            came for. It named the item catalog as living elsewhere until Items moved in; a
            sentence that outlives the arrangement it describes is worse than none.
          */}
          <p className="text-xs text-slate-500">
            The item catalog, the vocabularies it is built from, and the places stock sits.
            Stock itself is recorded on the Batches, Units and Levels pages.
          </p>

          <SettingsMenu entries={ENTRIES} />
        </div>
      </div>
    </div>
  );
}
